import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Form, Input, Button, Typography, Card } from "antd";
import { UserOutlined, LockOutlined, LoginOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { PublicLayout } from "../shared/components/layout";
import { useAuth } from "../shared/contexts";
import { getFetch, API_URL } from "../shared/utils/Util";
import { useAntdTheme } from "../shared/hooks";

const { Title, Text } = Typography;

interface Restaurant {
  id: string;
  roles?: string[];
}

interface LoginFormValues {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { authState, login, selectRestaurant } = useAuth();
  const [loginError, setLoginError] = useState("");

  // Redirect if already authenticated with a selected restaurant
  useEffect(() => {
    if (authState.isAuthenticated && authState.currentRestaurant) {
      navigate("/", { replace: true });
    }
  }, [authState.isAuthenticated, authState.currentRestaurant]);

  const onFinish = (values: LoginFormValues) => {
    // Store credentials temporarily for the API call
    sessionStorage.setItem("username", values.username);
    sessionStorage.setItem("password", values.password);

    const handleResp = (restaurants: Restaurant[]) => {
      if (restaurants.length === 0) {
        setLoginError(t("no_restaurants_for_account"));
        sessionStorage.removeItem("username");
        sessionStorage.removeItem("password");
        return;
      }

      // Use AuthContext to manage login state
      login(values.username, values.password, restaurants);

      if (restaurants.length === 1) {
        // Single restaurant: auto-select and redirect to home
        selectRestaurant(restaurants[0].id);
        navigate("/", { replace: true });
      } else {
        // Multiple restaurants: navigate to selection page
        navigate("/select-restaurant", { replace: true });
      }
    };

    const handleError = () => {
      setLoginError(t("failed_to_login"));
      sessionStorage.removeItem("username");
      sessionStorage.removeItem("password");
    };

    getFetch(`${API_URL}/v1/u/restaurants`, handleResp, handleError);
  };

  return (
    <PublicLayout>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: token.colorPrimary,
          padding: token.paddingLG,
        }}
      >
        <Card
          style={{
            width: "100%",
            maxWidth: "450px",
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadow,
          }}
          styles={{
            body: {
              padding: token.paddingXL,
            },
          }}
        >
          <div style={{ textAlign: "center", marginBottom: token.paddingXL }}>
            <Title
              level={1}
              style={{
                color: token.colorPrimary,
                marginBottom: token.paddingXS,
                fontSize: token.fontSizeHeading1,
              }}
            >
              TicketRails
            </Title>
            <Text
              type="secondary"
              style={{
                fontSize: token.fontSize,
              }}
            >
              {t("order_management_system")}
            </Text>
          </div>

          <Form onFinish={onFinish} layout="vertical" size="large">
            <Form.Item
              name="username"
              rules={[{ required: true, message: t("please_enter_account") }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: token.colorTextTertiary }} />}
                placeholder={t("account")}
                autoComplete="email"
                style={{
                  borderRadius: token.borderRadiusLG,
                  height: token.controlHeightLG,
                }}
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[{ required: true, message: t("please_enter_password") }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: token.colorTextTertiary }} />}
                type="password"
                placeholder={t("password")}
                autoComplete="current-password"
                style={{
                  borderRadius: token.borderRadiusLG,
                  height: token.controlHeightLG,
                }}
              />
            </Form.Item>
            {loginError && (
              <Alert
                message={loginError}
                type="error"
                showIcon
                style={{
                  marginBottom: token.marginLG,
                  borderRadius: token.borderRadiusLG,
                }}
              />
            )}
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<LoginOutlined />}
                style={{
                  width: "100%",
                  height: token.controlHeightLG,
                  borderRadius: token.borderRadiusLG,
                  fontSize: token.fontSizeLG,
                  fontWeight: token.fontWeightStrong,
                  marginTop: token.marginMD,
                }}
              >
                {t("login")}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <div style={{ textAlign: "center", marginTop: token.marginXL }}>
          <Text
            style={{
              color: token.colorTextSecondary,
              fontSize: token.fontSizeSM,
            }}
          >
            © {new Date().getFullYear()} TicketRails. All rights reserved.
          </Text>
        </div>
      </div>
    </PublicLayout>
  );
};

export default Login;
